import React, { useState, useRef, useEffect, useCallback } from "react";

const isImageFile = (file) => {
  if (file instanceof File) return file.type.startsWith("image/");
  const ext = file.filename?.split(".").pop()?.toLowerCase();
  return ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext);
};

const FileTile = ({ name, previewUrl, isImage, progress, error, onRemove, url }) => (
  <div className="flex flex-col gap-1">
    <div className="relative group">
      <div
        className="aspect-square rounded overflow-hidden bg-gray-100 border-2 border-gray-200 cursor-pointer"
        onClick={() => url && window.open(url, "_blank", "noopener,noreferrer")}
      >
        {isImage && previewUrl ? (
          <img src={previewUrl} alt={name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className="fa-regular fa-file text-3xl text-secondary" aria-hidden="true" />
          </div>
        )}
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute top-2 right-2 bg-danger text-white rounded-full w-6 h-6 flex items-center justify-center"
          aria-label={`Remove ${name}`}
        >
          <i className="fa-regular fa-xmark text-sm" aria-hidden="true" />
        </button>
      )}
    </div>
    <p className="text-sm truncate">{name}</p>
    {progress !== null && progress !== undefined && !error && (
      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-brand rounded-full transition-all duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>
    )}
    {error && <p className="text-xs text-danger">{error}</p>}
  </div>
);

const MultiFileUpload = ({
  fieldName,
  removeFieldName,
  existingFiles = [],
  accept,
  multiple = true,
  label = "Upload Files",
  directUploadUrl,
}) => {
  const [pendingFiles, setPendingFiles] = useState([]);
  const [filesToRemove, setFilesToRemove] = useState([]);
  const fileInputRef = useRef(null);
  // Maps ActiveStorage upload id → file name so we can update progress state
  const uploadIdMap = useRef({});
  const pendingFilesRef = useRef(pendingFiles);
  pendingFilesRef.current = pendingFiles;

  // Sync pending files to hidden input via DataTransfer so the parent form includes them
  useEffect(() => {
    if (!fileInputRef.current) return;
    if (pendingFiles.length === 0) {
      fileInputRef.current.value = "";
      return;
    }
    const dt = new DataTransfer();
    pendingFiles.forEach(({ file }) => {
      if (file instanceof File) dt.items.add(file);
    });
    fileInputRef.current.files = dt.files;
  }, [pendingFiles]);

  // Revoke object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      pendingFilesRef.current.forEach(({ objectUrl }) => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      });
    };
  }, []);

  // Hook into ActiveStorage direct-upload events for per-file progress
  useEffect(() => {
    const onInitialize = ({ detail: { id, file } }) => {
      uploadIdMap.current[id] = file.name;
    };

    const onProgress = ({ detail: { id, progress } }) => {
      const name = uploadIdMap.current[id];
      if (!name) return;
      setPendingFiles((prev) =>
        prev.map((pf) => (pf.file.name === name ? { ...pf, progress } : pf))
      );
    };

    const onError = ({ detail: { id, error } }) => {
      const name = uploadIdMap.current[id];
      if (!name) return;
      setPendingFiles((prev) =>
        prev.map((pf) => (pf.file.name === name ? { ...pf, error } : pf))
      );
    };

    const onEnd = ({ detail: { id } }) => {
      const name = uploadIdMap.current[id];
      if (!name) return;
      setPendingFiles((prev) =>
        prev.map((pf) => (pf.file.name === name ? { ...pf, progress: 100 } : pf))
      );
    };

    window.addEventListener("direct-upload:initialize", onInitialize);
    window.addEventListener("direct-upload:progress", onProgress);
    window.addEventListener("direct-upload:error", onError);
    window.addEventListener("direct-upload:end", onEnd);
    return () => {
      window.removeEventListener("direct-upload:initialize", onInitialize);
      window.removeEventListener("direct-upload:progress", onProgress);
      window.removeEventListener("direct-upload:error", onError);
      window.removeEventListener("direct-upload:end", onEnd);
    };
  }, []);

  const handleFileSelect = useCallback(
    (event) => {
      const files = Array.from(event.target.files);
      const newFiles = files.map((file) => {
        const objectUrl = URL.createObjectURL(file);
        return {
          uuid: crypto.randomUUID(),
          file,
          name: file.name,
          progress: null,
          error: null,
          objectUrl,
          previewUrl: isImageFile(file) ? objectUrl : null,
        };
      });
      setPendingFiles((prev) => (multiple ? [...prev, ...newFiles] : newFiles));
      event.target.value = "";
    },
    [multiple]
  );

  const handleRemovePending = useCallback((uuid) => {
    setPendingFiles((prev) => {
      const removed = prev.find((pf) => pf.uuid === uuid);
      if (removed?.objectUrl) URL.revokeObjectURL(removed.objectUrl);
      return prev.filter((pf) => pf.uuid !== uuid);
    });
  }, []);

  const handleRemoveExisting = useCallback((id) => {
    setFilesToRemove((prev) => [...prev, id]);
  }, []);

  const visibleExistingFiles = existingFiles.filter(
    (f) => !filesToRemove.includes(f.id)
  );
  const hasFiles = visibleExistingFiles.length > 0 || pendingFiles.length > 0;

  return (
    <div className="space-y-4">
      {hasFiles && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {visibleExistingFiles.map((file) => (
            <FileTile
              key={file.id}
              name={file.filename}
              previewUrl={isImageFile(file) ? file.url : null}
              isImage={isImageFile(file)}
              url={file.url}
              progress={null}
              error={null}
              onRemove={() => handleRemoveExisting(file.id)}
            />
          ))}
          {pendingFiles.map(({ uuid, name, previewUrl, objectUrl, file, progress, error }) => (
            <FileTile
              key={uuid}
              name={name}
              previewUrl={previewUrl}
              isImage={isImageFile(file)}
              url={objectUrl}
              progress={progress}
              error={error}
              onRemove={progress === null ? () => handleRemovePending(uuid) : null}
            />
          ))}
        </div>
      )}

      <label className="inline-flex items-center gap-2 cursor-pointer text-brand font-semibold hover:underline">
        <i className="fa-regular fa-upload" aria-hidden="true" />
        <span>{hasFiles && multiple ? "Add more files" : label}</span>
        <input
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileSelect}
          className="hidden"
        />
      </label>

      {/* Hidden input kept in sync with pendingFiles for the parent form POST */}
      <input
        ref={fileInputRef}
        type="file"
        name={fieldName}
        multiple={multiple}
        data-direct-upload-url={directUploadUrl}
        data-direct-upload-no-ui="true"
        style={{ display: "none" }}
        aria-hidden="true"
      />

      {/* Removal tracking — one hidden input per removed existing file */}
      {removeFieldName &&
        filesToRemove.map((id) => (
          <input key={id} type="hidden" name={removeFieldName} value={id} />
        ))}
    </div>
  );
};

export default MultiFileUpload;
